import { Heading } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import {
  AvailablePage,
  AvailablePageWithUniqueName,
} from '@mm/state/interfaces/connected-page';
import CompanyModel from '@mm/state/model/CompanyModel';
import modalActions, {
  OpenConnectPagesModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectCurrentUserId } from '@mm/state/selectors/userSelector';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Field, withFormik } from 'formik';
import { debounce, first, get } from 'lodash';
import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withState } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import CheckBox from '../../components/Checkbox';
import { SearchInput } from '../../components/SearchInput/SearchInput';
import { PAGES_UPGRADE_COUNT } from '../../constants/payments';
import { CHATBOT_EDITOR } from '../../constants/routes';
import { RouterComponentProps } from '../../helpers/routes/interfaces';
import { useInfiniteScroll } from '../../hooks/use-infinite-scroll';
import { ModalInstanceProps } from '../interfaces';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProcessingBlinder,
} from '../modal-helpers';
import './ConnectPagesModal.scss';
import { defaultSelectAllPageCountThreshold, modalHeader } from './constants';
import { addIdToNonUniqueNamePages } from './helpers';

const CheckBoxField = props => {
  const {
    field: { value, onChange, name },
    isConnected,
    label,
    className,
  } = props;

  const handleToggle = (e: any) => {
    e.target.value = e.target.checked ? true : false;
    onChange(e);
  };

  return (
    <CheckBox
      disabled={isConnected}
      isChecked={value || isConnected}
      onToggle={handleToggle}
      label={label}
      name={name}
      className={className}
    />
  );
};

const ConnectPage = ({
  field: { value, name },
  canSelectMultiplePages,
  onPageConnectClick = (_: AvailablePageWithUniqueName) => {},
  connectedPageButton,
  closeModal,
}) => {
  const { isConnected, page } = value;
  const { uniqueName, userId, ...originalPage } = page;

  const currentUserId = useSelector(selectCurrentUserId);
  const connectedText =
    userId === currentUserId ? 'Connected' : 'Connected by a another user';

  const label = uniqueName || originalPage.name;

  const connectedState =
    connectedPageButton && isConnected ? (
      <PrimaryButton
        color={ButtonColor.blue}
        onClick={() => connectedPageButton?.onClick(page, closeModal)}
      >
        {connectedPageButton?.text}
      </PrimaryButton>
    ) : (
      <div className="connect-pages-modal__connected-text">
        {isConnected && (
          <>
            <div className="connect-pages-modal__connected-icon" />
            <span>{connectedText}</span>
          </>
        )}
      </div>
    );

  if (!canSelectMultiplePages) {
    return (
      <div className="connect-pages-modal__item">
        {label}
        {connectedState}
        {!isConnected && (
          <PrimaryButton
            color={ButtonColor.blue}
            onClick={() => onPageConnectClick(page)}
          >
            Connect
          </PrimaryButton>
        )}
      </div>
    );
  }

  return (
    <div className="connect-pages-modal__item">
      <Field
        name={`${name}.selected`}
        component={CheckBoxField}
        isConnected={isConnected}
        label={uniqueName || originalPage.name}
        className="connect-pages-modal__page-name"
      />
      {connectedState}
    </div>
  );
};

type OuterProps = OpenConnectPagesModalParams & ModalInstanceProps;

type StateAndHandlers = {
  pagesChecked: any[];
  setPagesChecked: (pagesChecked: any[]) => void;
};

type Handlers = {};

type FormikProps = {
  values: ReturnType<typeof mapPropsToValues>;
  handleSubmit: typeof connectPagesAndRedirect;
  setFieldValue: (name: string, value: any) => void;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  Handlers &
  FormikProps &
  StateAndHandlers &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

export const ConnectPagesModal = (props: Props) => {
  const {
    isBusy,
    values,
    handleSubmit,
    isAllConnected,
    company,
    retrieveMorePages,
    availablePages,
    canSelectMultiplePages = true,
    closeModal,
    addPagesToBackend,
    onError,
    onSuccess,
    setFieldValue,
    setPagesChecked,
    standaloneToken,
    destinationPageId,
    title,
  } = props;
  const { next } = availablePages;
  const { pages } = values;
  const [query, setQuery] = useState('');
  const { openPermissionsHelperModal, openConnectPagesModal } =
    useModalActions();

  useEffect(() => {
    retrieveMorePages({});
  }, []);

  const fetchQuery = useRef(
    debounce(async (query: string) => {
      await retrieveMorePages({ searchQuery: query, nextCursor: '' });
    }, 1000),
  ).current;

  const onSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    fetchQuery(e.target.value);
  };

  const capturePagesChecked = () => {
    setPagesChecked(pages.map(p => p.selected));
  };

  const onFindMorePages = () => {
    openPermissionsHelperModal(
      () => openConnectPagesModal({ company }),
      'discover',
      'connect-follow-up',
    );
  };

  const fetchMore = useCallback(async () => {
    if (next) {
      if (canSelectMultiplePages) {
        capturePagesChecked();
      }
      await retrieveMorePages({ nextCursor: next, searchQuery: query });
    }
  }, [next, query, capturePagesChecked, retrieveMorePages]);

  const { scrollContainerRef } = useInfiniteScroll<HTMLDivElement>({
    fetchDisabled: !next,
    fetchMore,
  });

  const onSelectAll = () => {
    const { data } = availablePages;
    data.forEach((p, i) => {
      setFieldValue(`pages[${i}].selected`, true);
    });
  };

  const headingTitle = company?.name
    ? `${modalHeader} ${company.name}`
    : 'Connect your Facebook page';

  const text = title || headingTitle;

  const onPageConnectClick = async (page: AvailablePageWithUniqueName) => {
    try {
      const res = await addPagesToBackend(
        [page],
        company,
        true,
        standaloneToken,
        destinationPageId,
      );
      const connectedPage = first(res)?.facebookPage;
      if (connectedPage) {
        onSuccess && onSuccess(connectedPage);
      }
    } catch (e) {
      onError && onError(e);
    }
    closeModal();
  };

  return (
    <div className="connect-pages-modal">
      <ModalHeader className="connect-pages-modal__header">
        <Heading size={2} text={text} />
        {canSelectMultiplePages &&
          pages.length >= defaultSelectAllPageCountThreshold && (
            <Button onClick={onSelectAll} link>
              Select All
            </Button>
          )}
      </ModalHeader>

      <ModalBody className="connect-pages-modal__body" ref={scrollContainerRef}>
        <SearchInput
          className="connect-pages-modal__search"
          value={query}
          onChange={onSearchInputChange}
          placeholder="Search..."
        />
        {pages.map((page, i) => {
          const name = `pages[${i}]`;
          return (
            <Field
              key={i}
              name={name}
              component={innerProps => (
                <ConnectPage
                  {...innerProps}
                  {...props}
                  canSelectMultiplePages={canSelectMultiplePages}
                  onPageConnectClick={onPageConnectClick}
                />
              )}
            />
          );
        })}
      </ModalBody>

      <ModalFooter className="connect-pages-modal__footer">
        {canSelectMultiplePages && (
          <>
            <span
              className="connect-pages-modal__connect-more"
              onClick={onFindMorePages}
            >
              Don't see your page?
            </span>
            <PrimaryButton
              onClick={
                handleSubmit as (e?: MouseEvent<HTMLButtonElement>) => any
              }
              disabled={isAllConnected}
            >
              Connect Facebook Pages
            </PrimaryButton>
          </>
        )}
      </ModalFooter>

      <ModalProcessingBlinder show={isBusy} />
    </div>
  );
};

const isAllConnectedSelector = (state: StoreState) =>
  state.connectedPage.availablePages.data.every(p => !!p.facebookPageId);

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  addPagesToBackend: (
    pages: AvailablePage[],
    company: CompanyModel,
    throwException?: boolean,
    standaloneToken?: string,
    destinationPageId?: number,
    igAccountId?: string,
  ) =>
    dispatch(
      connectedPageCRUDActions.addPagesToBackend({
        pages,
        companyId: company.id,
        extraDataParams: {
          withSubscription: true,
          withFacebookErrors: true,
        },
        throwException,
        standaloneToken,
        destinationPageId,
        igAccountId,
      }),
    ),
  retrieveMorePages: ({
    nextCursor,
    searchQuery,
  }: {
    nextCursor?: string;
    searchQuery?: string;
  }) =>
    dispatch(
      connectedPageCRUDActions.retrieveAvailablePages(
        25,
        nextCursor,
        searchQuery,
      ),
    ),
  clearPages: () => dispatch(connectedPageCRUDActions.clearAvailablePages()),
  activateCompany: (entityId: number, id: number) =>
    dispatch(usersCRUDActions.updateActiveCompanyId(entityId, id)),
  openConnectPagesModal: (company: CompanyModel) =>
    dispatch(modalActions.openConnectPagesModal({ company })),
  showUpgradeModal: (landmineName: string) =>
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'connectPageUnlimitedPages',
      }),
    ),
});

const mapStateToProps = (state: StoreState) => ({
  availablePages: state.connectedPage.availablePages || {
    data: [],
    next: null,
    prev: null,
  },
  isAllConnected: isAllConnectedSelector(state),
  isBusy: state.connectedPage.busy,
  activeCompany: activeCompanySelector(state),
  session: state.session.session,
  featureFlags: combinedFeatureFlagsSelector(state),
});

const capturePagesChecked =
  ({ setPagesChecked, values: { pages } }: Props) =>
  () => {
    setPagesChecked(pages.map(p => p.selected));
  };

const mapPropsToValues = ({ availablePages: { data }, pagesChecked }) => ({
  pages: addIdToNonUniqueNamePages(data).map((page, i) => ({
    page: { ...page },
    isConnected: !!page.facebookPageId,
    selected:
      !!page.facebookPageId ||
      (pagesChecked.length > i
        ? pagesChecked[i]
        : data.length < defaultSelectAllPageCountThreshold),
  })),
});

const connectPagesAndRedirect = async (values, { props }) => {
  const {
    addPagesToBackend,
    closeModal,
    company,
    history,
    activeCompany,
    activateCompany,
    session,
    showUpgradeModal,
    availablePages,
    featureFlags,
  } = props;

  const selectedNewPages = values.pages
    .filter(p => p.selected && !p.isConnected)
    .map(p => {
      const { uniqueName, ...originPage } = p.page;
      return originPage;
    });

  const response = await addPagesToBackend(selectedNewPages, company);

  const firstPageBotId = get(response, '[0].bot.id');

  if (firstPageBotId) {
    if (company.id !== activeCompany.id) {
      await activateCompany(session.id, company.id);
    }

    history.push(`${CHATBOT_EDITOR}/${firstPageBotId}/home`);
  }

  const needsUpgrade =
    company?.editable &&
    availablePages?.data.length > PAGES_UPGRADE_COUNT &&
    !featureFlags.connectPageUnlimitedPages;

  if (needsUpgrade) {
    showUpgradeModal(landmineNames['CONNECT-PAGES-MODAL']);
  } else {
    closeModal();
  }
};

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withState('pagesChecked', 'setPagesChecked', []),
  withFormik({
    handleSubmit: connectPagesAndRedirect,
    mapPropsToValues,
    enableReinitialize: true,
  }),
);

export default enhance(ConnectPagesModal);
